<main id="conteudo" class="dashboard-shell" tabindex="-1">
  @if (busy()) {
  <div class="loading-overlay" role="status" aria-live="polite" aria-label="Carregando dados">
    <div class="spinner"></div>
    <p>{{ busyLabel() }}</p>
  </div>
  }

  <app-dashboard-header
    [activePeriodLabel]="activePeriodLabel()"
    [operationsTotal]="periodBusiness()?.totals?.operationsTotal ?? 0"
    [busy]="busy()"
    [isWeekClosed]="isWeekClosed()"
    [isToday]="isQuickRange('today')"
    [isWeek]="isQuickRange('week')"
    [isMonth]="isQuickRange('month')"
    (quickRange)="applyQuickRange($event)"
    (print)="printReport()"
    (logout)="logout()"
    (settings)="toggleSettings()"
  />

  <app-dashboard-period-filter
    [startDate]="startDate()"
    [endDate]="endDate()"
    [activePeriodLabel]="activePeriodLabel()"
    [standardWeekHelp]="standardWeekHelp"
    [monthOptions]="monthOptions()"
    [selectedMonth]="selectedMonth()"
    [monthStatusLabel]="monthStatusLabel()"
    [busy]="loading() || processing() || syncing()"
    (monthChange)="setMonth($event)"
    (startDateChange)="setStartDate($event)"
    (endDateChange)="setEndDate($event)"
    (apply)="applyPeriod()"
    (resetDefault)="resetDefaultPeriod()"
  />

  <section class="print-report print-only" aria-label="Relatório impresso de fechamento">
    <header class="print-report-head">
      <div>
        <span class="print-kicker">Relatório de fechamento</span>
        <h1>Comissões Mercado Livre / Mercado Pago</h1>
        <p>Período: <strong>{{ activePeriodLabel() }}</strong></p>
      </div>
      <div class="print-meta">
        <strong>{{ isMpAccountMoneyReady(mpAccountMoney()) ? 'Extrato MP importado' : 'Extrato MP pendente' }}</strong>
        <span>Gerado em {{ printGeneratedAt() | date : 'dd/MM/yyyy HH:mm' }}</span>
      </div>
    </header>

    @if (mpAccountMoney(); as cash) {
    <section class="print-block print-commission-block">
      <div class="print-block-title">
        <span>Base oficial para pagamento</span>
        <h2>Comissão pelo dinheiro liberado no Mercado Pago</h2>
        <p>
          Base = dinheiro liberado no Mercado Pago neste período
          ({{ cash.totals.operationalEntries | currency : 'BRL' : 'symbol' : '1.2-2' }})
          <strong>menos</strong> devoluções, estornos e ajustes que saíram do caixa no mesmo período
          ({{ cash.totals.operationalDebits | currency : 'BRL' : 'symbol' : '1.2-2' }}).
          Vendas D30 só entram quando o Mercado Pago liberar o dinheiro. Comissão nunca fica negativa.
        </p>
      </div>

      <div class="print-summary-grid">
        <article class="print-metric">
          <span>Vendido bruto liberado</span>
          <strong>{{ cash.totals.salesGross | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong>
        </article>
        <article class="print-metric">
          <span>Deduções nas vendas</span>
          <strong>{{ cash.totals.embeddedDeductionsTotal | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong>
          <small>tarifa + financiamento + frete + imposto</small>
        </article>
        <article class="print-metric">
          <span>(−) Devoluções/estornos que saíram</span>
          <strong>{{ cash.totals.operationalDebits | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong>
        </article>
        <article class="print-metric primary">
          <span>Base disponível confirmada (líquida)</span>
          <strong>{{ cash.totals.operationalNet | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong>
          <small>dinheiro liberado − devoluções/estornos</small>
        </article>
        <article class="print-metric">
          <span>Rodrigo</span>
          <strong>{{ cash.totals.commissionRodrigo | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong>
          <small>{{ employeeRate('rodrigo', 0.03) | percent : '1.0-0' }} da base</small>
        </article>
        <article class="print-metric">
          <span>Maria</span>
          <strong>{{ cash.totals.commissionMaria | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong>
          <small>{{ employeeRate('maria', 0.01) | percent : '1.0-0' }} da base</small>
        </article>
        <article class="print-metric">
          <span>Status</span>
          <strong>{{ isMpAccountMoneyReady(cash) ? 'Pronto' : 'Pendente' }}</strong>
          <small>{{ cash.totals.movementsCount }} movimentos importados</small>
        </article>
      </div>
    </section>

    <section class="print-block">
      <h2>Conferência do caixa</h2>
      <div class="print-check-grid">
        <article><span>Entradas MP</span><strong>{{ cash.totals.entries | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong></article>
        <article><span>Saídas MP</span><strong>{{ cash.totals.exits | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong></article>
        <article><span>Variação líquida no período</span><strong>{{ cash.totals.netCash | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong></article>
        <article><span>Linhas do extrato</span><strong>{{ cash.totals.movementsCount }}</strong></article>
        <article><span>Vendas vinculadas</span><strong>{{ cash.totals.linkedOperationalCount }}</strong></article>
        <article><span>Movimentos sem venda vinculada</span><strong>{{ cash.totals.unlinkedOperationalCount }}</strong></article>
      </div>
    </section>

    <section class="print-block allow-break">
      <h2>Movimentos que precisam de atenção</h2>
      @if (printAttentionMovements(cash).length > 0) {
      <table class="print-table">
        <caption class="visually-hidden">Movimentos que precisam de atenção antes do pagamento da comissão</caption>
        <thead>
          <tr>
            <th scope="col">Data</th>
            <th scope="col">Pedido / produto</th>
            <th scope="col">Tipo</th>
            <th scope="col">Valor</th>
          </tr>
        </thead>
        <tbody>
          @for (row of printAttentionMovements(cash); track (row.sourceId ?? '') + (row.linkedOrderId ?? '') + row.amount) {
          <tr>
            <td>{{ formatDateTime(row.settlementDate) }}</td>
            <td>
              <strong>{{ row.linkedOrderId ?? 'Sem pedido vinculado' }}</strong>
              <div class="print-sub">{{ row.linkedItemTitle ?? row.sourceId ?? 'Conferir manualmente' }}</div>
            </td>
            <td>{{ formatMpAccountCategory(row.category) }}</td>
            <td class="print-money">{{ row.amount | currency : 'BRL' : 'symbol' : '1.2-2' }}</td>
          </tr>
          }
        </tbody>
      </table>
      } @else {
      <p class="print-note">Nenhum movimento da base ficou sem vínculo de venda neste período.</p>
      }
    </section>

    <section class="print-block allow-break">
      <h2>Memória de cálculo das vendas liberadas</h2>
      @if (printGoodSales(cash).length > 0) {
      <table class="print-table">
        <caption class="visually-hidden">Pedidos com saldo positivo no Mercado Pago</caption>
        <thead>
          <tr>
            <th scope="col">Pedido ML</th>
            <th scope="col">Produto</th>
            <th scope="col">Data venda</th>
            <th scope="col">Bruto</th>
            <th scope="col">Deduções</th>
            <th scope="col">Líquido</th>
          </tr>
        </thead>
        <tbody>
          @for (row of printGoodSales(cash); track row.orderId) {
          <tr>
            <td>{{ row.orderId }}</td>
            <td>{{ row.itemTitle ?? 'Produto sem título' }}</td>
            <td>{{ formatDateTime(row.saleDate) }}</td>
            <td class="print-money">{{ row.gross | currency : 'BRL' : 'symbol' : '1.2-2' }}</td>
            <td class="print-money">{{ row.embeddedDeductions | currency : 'BRL' : 'symbol' : '1.2-2' }}</td>
            <td class="print-money"><strong>{{ row.net | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong></td>
          </tr>
          }
        </tbody>
      </table>
      } @else {
      <p class="print-note">Nenhum pedido positivo encontrado no extrato importado.</p>
      }
    </section>

    <section class="print-block allow-break">
      <h2>Ajustes que reduziram a base</h2>
      @if (commissionAdjustmentGroups(cash).length > 0) {
      <table class="print-table">
        <caption class="visually-hidden">Ajustes posteriores agrupados por pedido no Mercado Pago</caption>
        <thead>
          <tr>
            <th scope="col">Pedido / produto</th>
            <th scope="col">Última data</th>
            <th scope="col">Mov.</th>
            <th scope="col">Débitos</th>
            <th scope="col">Créditos</th>
            <th scope="col">Efeito real</th>
          </tr>
        </thead>
        <tbody>
          @for (row of commissionAdjustmentGroups(cash); track row.key) {
          <tr>
            <td>
              <strong>{{ row.orderId ?? 'Sem pedido vinculado' }}</strong>
              <div class="print-sub">{{ row.itemTitle ?? row.sourceId ?? 'Conferir manualmente' }}</div>
            </td>
            <td>{{ formatDateTime(row.lastSettlementDate) }}</td>
            <td>{{ row.rowsCount }}</td>
            <td class="print-money">{{ row.debits | currency : 'BRL' : 'symbol' : '1.2-2' }}</td>
            <td class="print-money">{{ row.credits | currency : 'BRL' : 'symbol' : '1.2-2' }}</td>
            <td class="print-money"><strong>{{ row.net | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong></td>
          </tr>
          }
        </tbody>
      </table>
      } @else {
      <p class="print-note">Nenhum débito posterior reduziu a base neste período.</p>
      }
    </section>

    @if (periodBusiness(); as biz) {
    <section class="print-block">
      <h2>Resumo dos produtos conciliados</h2>
      <div class="print-check-grid compact">
        <article><span>Pedidos no período</span><strong>{{ biz.totals.operationsTotal }}</strong></article>
        <article><span>Pedidos positivos</span><strong>{{ biz.totals.profitableCount }}</strong></article>
        <article><span>Pedidos devolvidos</span><strong>{{ biz.totals.returnedCount }}</strong></article>
        <article><span>Pedidos cancelados</span><strong>{{ biz.totals.cancelledCount }}</strong></article>
      </div>
    </section>
    }

    <footer class="print-footer">
      <p>
        Fonte do pagamento: dinheiro liberado pelo Mercado Pago em pagamentos aprovados.
        A conciliação por pedido é usada para conferência de produto, mas vendas futuras/D30 só entram após a liberação no MP.
      </p>
    </footer>
    } @else {
    <section class="print-empty">
      <h2>Relatório indisponível</h2>
      <p>Não há extrato Mercado Pago salvo para {{ activePeriodLabel() }}. Atualize as informações antes de imprimir o fechamento.</p>
    </section>
    }
  </section>

  <section class="card command-panel no-print" aria-labelledby="refresh-period-title">
    <h2 id="refresh-period-title" class="visually-hidden">Atualização do período pela API</h2>
    <div class="command-copy">
      <span class="eyebrow">Fluxo do fechamento</span>
      <h2>Três passos para fechar sem se perder</h2>
      <p>Use este bloco como roteiro. Ele deixa claro quando só estamos lendo o banco e quando realmente estamos chamando as APIs.</p>
    </div>
    <div class="workflow-grid" aria-label="Etapas do fechamento">
      <article class="workflow-card">
        <strong>1</strong>
        <div>
          <h3>Escolha o período</h3>
          <p>Selecione o mês salvo ou monte uma semana manual para conferência.</p>
        </div>
      </article>
      <article class="workflow-card">
        <strong>2</strong>
        <div>
          <h3>Atualize só se precisar</h3>
          <p>Busque novidades apenas quando quiser puxar vendas novas, extrato ou ajustes.</p>
        </div>
      </article>
      <article class="workflow-card">
        <strong>3</strong>
        <div>
          <h3>Confira antes de pagar</h3>
          <p>Olhe comissão, extrato linha a linha e pedidos com atenção antes do sábado.</p>
        </div>
      </article>
    </div>
    <div class="command-actions">
      <button class="btn btn-primary" type="button" (click)="syncAll()" [disabled]="busy() || syncingAll()">
        {{ syncingAll() ? 'Atualizando...' : 'Atualizar fechamento' }}
      </button>
      <button class="btn btn-secondary" type="button" (click)="syncHistoricalFromJanuary()" [disabled]="busy() || syncingAll()">
        Carregar histórico completo desde janeiro
      </button>
      <a class="btn btn-ghost" [href]="exportUrl()" target="_blank" rel="noopener">Exportar XLSX</a>
    </div>

    @if (syncAllProgress(); as p) {
    <div class="sync-all-progress">
      <span>{{ p.doneSources }} de {{ p.totalSources }} fontes — {{ p.status }}</span>
      <ul>
        @for (s of p.sources; track s.source) {
        <li>
          {{ formatSyncSource(s.source) }}: {{ s.status }}
          @if (s.total) { ({{ s.processed }}/{{ s.total }}) }
          @if (s.error) { <span class="danger"> — {{ s.error }}</span> }
        </li>
        }
      </ul>
    </div>
    }
  </section>

  <section class="card tabs-bar no-print" role="tablist" aria-label="Seções do dashboard">
    <button id="tab-caixa" type="button" role="tab" class="tab-btn" [class.active]="tab() === 'caixa'" [attr.aria-selected]="tab() === 'caixa'" [attr.tabindex]="tab() === 'caixa' ? 0 : -1" aria-controls="panel-caixa" (click)="setTab('caixa')" (keydown)="handleTabKeydown($event, 'caixa')">Comissão e caixa</button>
    <button id="tab-movimentos" type="button" role="tab" class="tab-btn" [class.active]="tab() === 'movimentos'" [attr.aria-selected]="tab() === 'movimentos'" [attr.tabindex]="tab() === 'movimentos' ? 0 : -1" aria-controls="panel-movimentos" (click)="setTab('movimentos')" (keydown)="handleTabKeydown($event, 'movimentos')">Extrato linha a linha</button>
    <button id="tab-vendas" type="button" role="tab" class="tab-btn" [class.active]="tab() === 'vendas'" [attr.aria-selected]="tab() === 'vendas'" [attr.tabindex]="tab() === 'vendas' ? 0 : -1" aria-controls="panel-vendas" (click)="setTab('vendas')" (keydown)="handleTabKeydown($event, 'vendas')">Pedidos e produtos</button>
    <button id="tab-historico" type="button" role="tab" class="tab-btn" [class.active]="tab() === 'historico'" [attr.aria-selected]="tab() === 'historico'" [attr.tabindex]="tab() === 'historico' ? 0 : -1" aria-controls="panel-historico" (click)="setTab('historico')" (keydown)="handleTabKeydown($event, 'historico')">Histórico mensal</button>
  </section>

  <!-- ═══════════════ CAIXA ═══════════════ -->
  @if (tab() === 'caixa') {
  <section id="panel-caixa" class="tab-panel" role="tabpanel" aria-labelledby="tab-caixa" tabindex="-1">
    @if (mpAccountMoney(); as cash) {
    <section class="card payout-hero" [class.pending]="!isMpAccountMoneyReady(cash)">
      <div class="payout-copy">
        <span class="eyebrow">O que importa para o sábado</span>
        <h2>{{ isMpAccountMoneyReady(cash) ? 'Comissões prontas para conferência' : 'Ainda aguardando o Mercado Pago' }}</h2>
        <p>
          A comissão usa apenas vendas aprovadas cujo dinheiro já foi liberado no Mercado Pago.
          Se a venda é D30, ela fica fora até cair de verdade no caixa.
        </p>
      </div>
      <div class="payout-cards" aria-label="Valores principais para pagamento">
        <article class="payout-card base-card">
          <span>Base disponível confirmada</span>
          @if (isMpAccountMoneyReady(cash)) { <strong>{{ cash.totals.operationalNet | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong> } @else { <strong class="pending-value">Pendente</strong> }
        </article>
        <article class="payout-card">
          <span>Rodrigo</span>
          @if (isMpAccountMoneyReady(cash)) { <strong>{{ cash.totals.commissionRodrigo | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong> } @else { <strong class="pending-value">Aguardando</strong> }
          <small>{{ employeeRate('rodrigo', 0.03) | percent : '1.0-0' }} da base</small>
        </article>
        <article class="payout-card">
          <span>Maria</span>
          @if (isMpAccountMoneyReady(cash)) { <strong>{{ cash.totals.commissionMaria | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong> } @else { <strong class="pending-value">Aguardando</strong> }
          <small>{{ employeeRate('maria', 0.01) | percent : '1.0-0' }} da base</small>
        </article>
      </div>
    </section>
    } @else {
    <section class="card payout-hero pending">
      <div class="payout-copy">
        <span class="eyebrow">O que importa para o sábado</span>
        <h2>Sem extrato Mercado Pago salvo</h2>
        <p>Atualize os dados para pedir o extrato oficial do período. Sem ele, o sistema não deve sugerir pagamento de comissão.</p>
      </div>
      <div class="payout-cards">
        <article class="payout-card base-card"><span>Base disponível confirmada</span><strong class="pending-value">Pendente</strong></article>
        <article class="payout-card"><span>Rodrigo</span><strong class="pending-value">Aguardando</strong></article>
        <article class="payout-card"><span>Maria</span><strong class="pending-value">Aguardando</strong></article>
      </div>
    </section>
    }

    <section class="card help-card no-print">
      <div class="help-head">
        <h2>Entenda os números</h2>
        <button class="btn btn-secondary" type="button" [attr.aria-expanded]="showHelp()" aria-controls="dashboard-help-grid" (click)="toggleHelp()">{{ showHelp() ? 'Ocultar' : 'Entender os números' }}</button>
      </div>
      @if (showHelp()) {
      <div id="dashboard-help-grid" class="help-grid">
        <div><strong>Dinheiro disponível</strong><p>Só entra na comissão quando o Mercado Pago informa que o valor está realmente liberado/disponível.</p></div>
        <div><strong>D30 ainda fora</strong><p>Venda parcelada ou com prazo futuro só entra quando o dinheiro cair no Mercado Pago.</p></div>
        <div><strong>Cancelamentos</strong><p>Cancelamento, disputa e estorno não viram base positiva de comissão.</p></div>
        <div><strong>Base da comissão</strong><p>Soma dos créditos de venda que aparecem no extrato oficial Account Money importado.</p></div>
      </div>
      <p class="help-note">Fonte oficial da conferência: extrato real do Mercado Pago.</p>
      }
    </section>

    @if (mpAccountMoney(); as cash) {
    <section class="card mp-cash-panel simple-panel">
      <div class="section-title-row">
        <div>
          <span class="eyebrow">Verdade para conferência</span>
          <h2>Dinheiro disponível para comissão</h2>
          <p class="section-caption">
            A base abaixo só deve considerar dinheiro realmente disponível no Mercado Pago. Vendas D30 futuras ainda não entram.
          </p>
        </div>
        <span class="chip chip-emphasis">{{ cash.job?.status === 'DONE' ? 'Extrato importado' : 'Aguardando Mercado Pago' }}</span>
      </div>

      @if (!isMpAccountMoneyReady(cash)) {
      <div class="cash-alert" role="alert">
        <strong>Não use este período para pagar comissão ainda.</strong>
        <p>
          O extrato oficial do Mercado Pago ainda não foi importado completo para {{ activePeriodLabel() }}.
          Os valores abaixo podem estar parciais. Clique em <strong>Atualizar fechamento</strong> e aguarde o Mercado Pago liberar o arquivo.
        </p>
        @if (cash.job?.message) { <p class="muted-text">{{ cash.job?.message }}</p> }
      </div>
      }

      @if (cash.totals.movementsCount > 0) {
      <div class="kpi-grid">
        <article class="card kpi kpi-tone-received"><span>Vendido bruto liberado</span><strong>{{ cash.totals.salesGross | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong></article>
        <article class="card kpi kpi-tone-fee"><span>Deduções nas vendas</span><strong>{{ cash.totals.embeddedDeductionsTotal | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong></article>
        <article class="card kpi kpi-tone-fee"><span>Tarifa ML/MP</span><strong>{{ cash.totals.platformFeesTotal | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong></article>
        <article class="card kpi kpi-tone-fee"><span>Frete/financiamento</span><strong>{{ (cash.totals.shippingFeesTotal + cash.totals.financingFeesTotal) | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong></article>
        <article class="card kpi kpi-tone-return"><span>Débitos posteriores</span><strong>{{ commissionDebitTotal(cash) | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong></article>
        <article class="card kpi kpi-tone-net"><span>Variação líquida no período</span><strong>{{ cash.totals.netCash | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong></article>
        <article class="card kpi kpi-tone-net"><span>Base disponível confirmada</span><strong>{{ cash.totals.operationalNet | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong></article>
        <article class="card kpi kpi-tone-fixed"><span>Rodrigo</span><strong>{{ cash.totals.commissionRodrigo | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong></article>
        <article class="card kpi kpi-tone-fixed"><span>Maria</span><strong>{{ cash.totals.commissionMaria | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong></article>
      </div>

      <div class="cash-note">
        <strong>{{ isMpAccountMoneyReady(cash) ? 'Resumo oficial:' : 'Resumo parcial salvo:' }}</strong>
        A base de comissão é
        <strong>{{ cash.totals.operationalNet | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong>,
        calculada por
        <strong>líquido das vendas liberadas</strong>,
        mais
        <strong>créditos de ajuste</strong>
        e menos
        <strong>débitos posteriores</strong>
        informados no relatório de dinheiro liberado do Mercado Pago.
      </div>

      <section class="attention-panel" aria-labelledby="attention-panel-title">
        <div>
          <span class="eyebrow">Antes de pagar</span>
          <h3 id="attention-panel-title">O que precisa ser conferido antes do pagamento</h3>
        </div>
        <div class="attention-grid">
          <article class="attention-card" [class.warning]="!isMpAccountMoneyReady(cash)">
            <span>Extrato MP</span>
            <strong>{{ isMpAccountMoneyReady(cash) ? 'Completo' : 'Pendente' }}</strong>
            <p>{{ isMpAccountMoneyReady(cash) ? 'Pode usar como base oficial.' : 'Aguarde o arquivo oficial antes de pagar.' }}</p>
          </article>
          <article class="attention-card" [class.warning]="cash.totals.unlinkedOperationalCount > 0">
            <span>Sem venda vinculada</span>
            <strong>{{ cash.totals.unlinkedOperationalCount }}</strong>
            <p>Movimentos operacionais que precisam de conferência manual.</p>
          </article>
          <article class="attention-card">
            <span>Base disponível</span>
            <strong>{{ cash.totals.operationalEntries | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong>
            <p>Vendas liberadas mais créditos de ajuste antes dos débitos posteriores.</p>
          </article>
          <article class="attention-card muted">
            <span>Linhas do extrato</span>
            <strong>{{ cash.totals.movementsCount }}</strong>
            <p>Total de movimentos importados no Account Money.</p>
          </article>
        </div>
      </section>

      <div class="simple-grid">
        <article class="card simple-card">
          <h3>O que entrou na base?</h3>
          @for (row of cashInCategories(cash); track row.category) {
          <div class="reason-row">
            <div>
              <span class="chip" [ngClass]="mpCategoryToneClass(row.category)">{{ formatMpAccountCategory(row.category) }}</span>
              <p>{{ movementSimpleExplanation(row.category, row.entries) }}</p>
            </div>
            <strong>{{ row.entries | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong>
          </div>
          }
        </article>

        <article class="card simple-card">
          <h3>Conferência dos vínculos</h3>
          <div class="health-row"><span>Movimentos ligados a vendas</span><strong>{{ cash.totals.linkedOperationalCount }}</strong></div>
          <div class="health-row warning"><span>Precisam de atenção</span><strong>{{ cash.totals.unlinkedOperationalCount }}</strong></div>
          <div class="health-row muted"><span>Linhas usadas na base</span><strong>{{ cash.totals.movementsCount }}</strong></div>
          <p class="muted-text">Movimentos sem venda vinculada aparecem na tabela abaixo para conferência.</p>
        </article>
      </div>

      <section class="card table-wrap simple-table">
        <h2>Memória de cálculo das vendas liberadas</h2>
        <p class="section-caption">Cada linha mostra bruto da venda, deduções que o Mercado Pago informou e o líquido que entrou na base antes de débitos posteriores.</p>
        <table>
          <caption class="visually-hidden">Vendas liberadas com deduções informadas pelo Mercado Pago</caption>
          <thead><tr><th scope="col">Produto</th><th scope="col">Pedido ML</th><th scope="col">Data no extrato</th><th scope="col">Bruto</th><th scope="col">Tarifa ML/MP</th><th scope="col">Financ.</th><th scope="col">Frete</th><th scope="col">Líquido</th></tr></thead>
          <tbody>
            @for (row of commissionCreditMovements(cash); track (row.sourceId ?? '') + (row.settlementDate ?? '') + row.amount) {
            <tr>
              <td><strong>{{ row.linkedItemTitle ?? 'Produto sem título' }}</strong><div class="item-sub">ID MP: {{ row.sourceId ?? '-' }}</div></td>
              <td>{{ row.linkedOrderId ?? 'Sem vínculo' }}</td>
              <td>{{ formatDateTime(row.settlementDate) }}</td>
              <td>{{ row.transactionAmount | currency : 'BRL' : 'symbol' : '1.2-2' }}</td>
              <td>{{ row.platformFeeAmount | currency : 'BRL' : 'symbol' : '1.2-2' }}</td>
              <td>{{ row.financingFeeAmount | currency : 'BRL' : 'symbol' : '1.2-2' }}</td>
              <td>{{ row.shippingFeeAmount | currency : 'BRL' : 'symbol' : '1.2-2' }}</td>
              <td><strong>{{ row.amount | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong></td>
            </tr>
            }
          </tbody>
        </table>
      </section>

      <section class="card table-wrap simple-table">
        <h2>Ajustes que reduziram a base</h2>
        <p class="section-caption">Agrupado por pedido para mostrar o efeito real: débitos do Mercado Pago menos créditos que voltaram para a conta.</p>
        @if (commissionAdjustmentGroups(cash).length > 0) {
        <table>
          <caption class="visually-hidden">Ajustes posteriores agrupados que reduzem a base de comissão</caption>
          <thead><tr><th scope="col">Produto</th><th scope="col">Pedido ML</th><th scope="col">Última data</th><th scope="col">Mov.</th><th scope="col">Débitos</th><th scope="col">Créditos</th><th scope="col">Efeito real</th><th scope="col">Tipos</th></tr></thead>
          <tbody>
            @for (row of commissionAdjustmentGroups(cash); track row.key) {
            <tr>
              <td><strong>{{ row.itemTitle ?? 'Produto sem título' }}</strong><div class="item-sub">ID MP: {{ row.sourceId ?? '-' }}</div></td>
              <td>{{ row.orderId ?? 'Sem vínculo' }}</td>
              <td>{{ formatDateTime(row.lastSettlementDate) }}</td>
              <td>{{ row.rowsCount }}</td>
              <td class="negative-money">{{ row.debits | currency : 'BRL' : 'symbol' : '1.2-2' }}</td>
              <td class="positive-money">{{ row.credits | currency : 'BRL' : 'symbol' : '1.2-2' }}</td>
              <td [class.negative-money]="row.net < 0" [class.positive-money]="row.net > 0"><strong>{{ row.net | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong></td>
              <td>{{ row.reasons }}</td>
            </tr>
            }
          </tbody>
        </table>
        } @else {
        <div class="empty-state inline-empty"><strong>Nenhum débito posterior reduziu a base neste período.</strong></div>
        }
      </section>

      <section class="card table-wrap simple-table">
        <h2>Movimentos que merecem atenção</h2>
        <p class="section-caption">Liberações da base que ainda não foram vinculadas automaticamente a uma venda.</p>
        <table>
          <caption class="visually-hidden">Movimentos que merecem atenção no Mercado Pago</caption>
          <thead><tr><th scope="col">Venda/produto</th><th scope="col">ID operação MP</th><th scope="col">Valor bruto</th><th scope="col">Ajuste</th><th scope="col">Valor líquido</th><th scope="col">Motivo</th></tr></thead>
          <tbody>
            @for (row of attentionMovements(cash); track (row.sourceId ?? '') + (row.description ?? '') + row.amount) {
            <tr>
              <td><strong>{{ linkedSaleLabel(row) }}</strong><div class="item-sub">{{ row.linkedOrderId ? 'Pedido ' + row.linkedOrderId : 'Sem pedido vinculado' }}</div></td>
              <td>{{ row.sourceId ?? '-' }}</td>
              <td [class.negative-money]="row.transactionAmount < 0">{{ row.transactionAmount | currency : 'BRL' : 'symbol' : '1.2-2' }}</td>
              <td>{{ movementAdjustment(row) === 0 ? '-' : (movementAdjustment(row) | currency : 'BRL' : 'symbol' : '1.2-2') }}</td>
              <td [class.negative-money]="row.amount < 0"><strong>{{ row.amount | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong></td>
              <td>{{ movementSimpleExplanation(row.category, row.amount) }}</td>
            </tr>
            }
          </tbody>
        </table>
      </section>

      <section class="card table-wrap simple-table">
        <h2>Pedidos com saldo positivo</h2>
        <p class="section-caption">Pedidos que colocaram dinheiro na conta e ajudaram o resultado da semana.</p>
        <table>
          <caption class="visually-hidden">Vendas boas com resultado positivo no período</caption>
          <thead><tr><th scope="col">Produto</th><th scope="col">Pedido ML</th><th scope="col">Data venda</th><th scope="col">Data no extrato</th><th scope="col">Entrou</th><th scope="col">Resultado</th></tr></thead>
          <tbody>
            @for (row of goodSales(cash); track row.orderId) {
            <tr>
              <td><strong>{{ row.itemTitle ?? 'Produto sem título' }}</strong><div class="item-sub">IDs MP: {{ row.movementIds || '-' }}</div></td>
              <td>{{ row.orderId }}</td>
              <td>{{ formatDateTime(row.saleDate) }}</td>
              <td>{{ formatDateTime(row.lastSettlementDate) }}</td>
              <td>{{ row.entries | currency : 'BRL' : 'symbol' : '1.2-2' }}</td>
              <td><strong>{{ row.net | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong></td>
            </tr>
            }
          </tbody>
        </table>
      </section>
      } @else {
      <div class="empty-state">
        <strong>Extrato Mercado Pago ainda não importado.</strong>
        <p>Ao aplicar o período, o sistema solicita o relatório oficial. Se o Mercado Pago ainda estiver processando, aplique novamente em alguns instantes.</p>
      </div>
      }
    </section>
    } @else {
    <section class="card empty-state">
      <strong>Ainda não há extrato salvo para {{ activePeriodLabel() }}.</strong>
      <p>
        Para pagar comissão com segurança, clique em <strong>Atualizar fechamento</strong>.
        O sistema vai buscar as vendas, pedir o extrato oficial do Mercado Pago e recalcular a conferência.
      </p>
    </section>
    }

    <section class="card integration-bar no-print">
      <div>
        <h2>Integração Mercado Livre</h2>
        @if (mlStatus(); as status) {
        <p>
          {{ status.connected ? 'Conectado' : 'Não conectado' }}
          @if (status.nickname) { <span> · {{ status.nickname }}</span> }
          @if (!status.hasConfig) { <span> · aguardando keys para testes</span> }
        </p>
        } @else { <p>Status indisponível.</p> }
      </div>
      @if (!(mlStatus()?.connected)) {
            <button class="btn btn-secondary" type="button" (click)="connectMercadoLivre()">Conectar ML/MP</button>
      }
    </section>

    <section class="card actions no-print">
      <p>{{ loading() ? 'Carregando dados salvos...' : 'Troque o período para ver o que já está salvo. Use "Atualizar fechamento" para buscar vendas e dados financeiros.' }}</p>
      <a class="btn btn-secondary" [href]="exportUrl()" target="_blank" rel="noopener">Exportar XLSX</a>
      @if (message()) { <p>{{ message() }}</p> }
      @if (error()) { <p class="error">{{ error() }}</p> }
    </section>
  </section>
  }

  <!-- ═══════════════ MOVIMENTOS DETALHADOS ═══════════════ -->
  @if (tab() === 'movimentos') {
  <section id="panel-movimentos" class="tab-panel" role="tabpanel" aria-labelledby="tab-movimentos" tabindex="-1">
    @if (mpAccountMoney(); as cash) {
    <section class="card table-wrap detailed-movements-panel">
      <div class="section-title-row">
        <div>
          <span class="eyebrow">Conferência linha a linha</span>
          <h2>Liberações linha a linha</h2>
          <p class="section-caption">
            Aqui você vê cada liberação que entrou na base de comissão e, quando possível, o pedido do Mercado Livre ligado a ela.
            Cancelamentos, disputas e vendas ainda não liberadas não viram base positiva.
          </p>
        </div>
        <span class="chip chip-emphasis">{{ cash.recentMovements.length }} movimentos</span>
      </div>

      <section class="movements-summary-grid" aria-label="Resumo dos movimentos do período">
        <article class="movement-summary-card good">
          <span>Entradas liberadas (vendas que caíram)</span>
          <strong>{{ cash.totals.operationalEntries | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong>
          <small>{{ commissionCreditMovements(cash).length }} venda(s) liberada(s) no período</small>
        </article>
        <article class="movement-summary-card bad">
          <span>(−) Devoluções/estornos liberados</span>
          <strong>{{ cash.totals.operationalDebits | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong>
          <small>{{ commissionDebitMovements(cash).length }} devolução(ões)/estorno(s) que saíram</small>
        </article>
        <article class="movement-summary-card neutral">
          <span>= Base líquida da comissão</span>
          <strong>{{ cash.totals.operationalNet | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong>
          <small>o que sobrou de venda no caixa (piso zero)</small>
        </article>
      </section>

      <div class="movement-inline-note">
        <strong>Leitura rápida:</strong>
        a comissão usa o dinheiro de vendas que caiu no Mercado Pago no período <strong>menos</strong> as devoluções e estornos
        que saíram no mesmo período. Saques, Pix e transferências não entram (são movimento de caixa, não resultado de venda).
      </div>

      <section class="movement-section">
        <div class="movement-section-head">
          <div>
            <h3>Entradas que entram na comissão</h3>
            <p class="section-caption">Créditos de venda encontrados no relatório oficial de dinheiro liberado deste período.</p>
          </div>
          <div class="movement-section-total">
            <span>Subtotal das linhas visíveis</span>
            <strong>{{ visibleMovementsNet(commissionCreditMovements(cash)) | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong>
          </div>
        </div>

        @if (commissionCreditMovements(cash).length > 0) {
        <table class="detailed-movements-table positive-table">
          <caption class="visually-hidden">Entradas do Mercado Pago que contam para a comissão</caption>
          <thead>
            <tr>
              <th scope="col">Data</th>
              <th scope="col">ID operação</th>
              <th scope="col">Venda vinculada</th>
              <th scope="col">Bruto</th>
              <th scope="col">Tarifa</th>
              <th scope="col">Financ.</th>
              <th scope="col">Frete</th>
              <th scope="col">Valor líquido</th>
            </tr>
          </thead>
          <tbody>
            @for (row of commissionCreditMovements(cash); track (row.sourceId ?? '') + (row.settlementDate ?? '') + row.amount) {
            <tr>
              <td>{{ formatDateTime(row.settlementDate) }}</td>
              <td>{{ row.sourceId ?? '-' }}</td>
              <td>
                <strong>{{ row.linkedOrderId ?? 'Sem venda vinculada' }}</strong>
                <div class="item-sub">{{ row.linkedItemTitle ?? 'Produto não identificado' }}</div>
              </td>
              <td>{{ row.transactionAmount | currency : 'BRL' : 'symbol' : '1.2-2' }}</td>
              <td>{{ row.platformFeeAmount | currency : 'BRL' : 'symbol' : '1.2-2' }}</td>
              <td>{{ row.financingFeeAmount | currency : 'BRL' : 'symbol' : '1.2-2' }}</td>
              <td>{{ row.shippingFeeAmount | currency : 'BRL' : 'symbol' : '1.2-2' }}</td>
              <td class="positive-money">
                <strong>{{ row.amount | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong>
                <div class="item-sub">{{ mpMovementPlain(row) }}</div>
              </td>
            </tr>
            }
          </tbody>
        </table>
        } @else {
        <div class="empty-state inline-empty">
          <strong>Nenhuma entrada operacional encontrada nas linhas visíveis.</strong>
        </div>
        }
      </section>

      <section class="movement-section">
        <div class="movement-section-head">
          <div>
            <h3>Ajustes agrupados por pedido</h3>
            <p class="section-caption">Aqui o Mercado Pago pode lançar mediação, reserva e crédito de volta no mesmo pedido. O que vale para a comissão é o efeito real.</p>
          </div>
          <div class="movement-section-total danger">
            <span>Efeito real agrupado</span>
            <strong>{{ visibleGroupsNet(commissionAdjustmentGroups(cash)) | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong>
          </div>
        </div>

        @if (commissionAdjustmentGroups(cash).length > 0) {
        <table class="detailed-movements-table negative-table">
          <caption class="visually-hidden">Ajustes agrupados da base liberada do Mercado Pago</caption>
          <thead>
            <tr>
              <th scope="col">Última data</th>
              <th scope="col">ID operação</th>
              <th scope="col">Venda vinculada</th>
              <th scope="col">Mov.</th>
              <th scope="col">Débitos</th>
              <th scope="col">Créditos</th>
              <th scope="col">Efeito real</th>
              <th scope="col">Tipos</th>
            </tr>
          </thead>
          <tbody>
            @for (row of commissionAdjustmentGroups(cash); track row.key) {
            <tr class="loss">
              <td>{{ formatDateTime(row.lastSettlementDate) }}</td>
              <td>{{ row.sourceId ?? '-' }}</td>
              <td>
                <strong>{{ row.orderId ?? 'Sem venda vinculada' }}</strong>
                <div class="item-sub">{{ row.itemTitle ?? (row.orderId ? 'Produto não identificado' : 'Conferir manualmente') }}</div>
              </td>
              <td>{{ row.rowsCount }}</td>
              <td class="negative-money">{{ row.debits | currency : 'BRL' : 'symbol' : '1.2-2' }}</td>
              <td class="positive-money">{{ row.credits | currency : 'BRL' : 'symbol' : '1.2-2' }}</td>
              <td [class.negative-money]="row.net < 0" [class.positive-money]="row.net > 0"><strong>{{ row.net | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong></td>
              <td>{{ row.reasons }}</td>
            </tr>
            }
          </tbody>
        </table>
        } @else {
        <div class="empty-state inline-empty">
          <strong>Nenhum débito entrou na base liberada deste período.</strong>
        </div>
        }
      </section>

      <section class="movement-section">
        <div class="movement-section-head">
          <div>
            <h3>Linhas só para conferência</h3>
            <p class="section-caption">Movimentos da base que ainda precisam de vínculo manual com pedido/produto.</p>
          </div>
          <div class="movement-section-total neutral">
            <span>Total líquido das linhas visíveis</span>
            <strong>{{ visibleMovementsNet(reconciliationMovements(cash)) | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong>
          </div>
        </div>

        @if (reconciliationMovements(cash).length > 0) {
        <table class="detailed-movements-table neutral-table">
          <caption class="visually-hidden">Linhas de conferência fora da comissão</caption>
          <thead>
            <tr>
              <th scope="col">Data</th>
              <th scope="col">ID operação</th>
              <th scope="col">Venda vinculada</th>
              <th scope="col">Categoria</th>
              <th scope="col">Explicação</th>
              <th scope="col">Valor líquido</th>
            </tr>
          </thead>
          <tbody>
            @for (row of reconciliationMovements(cash); track (row.sourceId ?? '') + (row.settlementDate ?? '') + row.amount + (row.transactionType ?? '')) {
            <tr>
              <td>{{ formatDateTime(row.settlementDate) }}</td>
              <td>{{ row.sourceId ?? '-' }}</td>
              <td>
                <strong>{{ row.linkedOrderId ?? 'Sem venda vinculada' }}</strong>
                <div class="item-sub">{{ row.linkedItemTitle ?? (row.linkedOrderId ? 'Produto não identificado' : 'Conferir manualmente') }}</div>
              </td>
              <td>
                <span class="chip tone-neutral">{{ formatMpTransactionType(row.description ?? row.transactionType) }}</span>
                @if (row.category) {
                <div class="tech-code">{{ formatMpAccountCategory(row.category) }}</div>
                }
              </td>
              <td>{{ mpMovementPlain(row) }}</td>
              <td [class.negative-money]="row.amount < 0" [class.positive-money]="row.amount > 0">
                <strong>{{ row.amount | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong>
              </td>
            </tr>
            }
          </tbody>
        </table>
        } @else {
        <div class="empty-state inline-empty">
          <strong>Nenhuma linha extra de conferência neste período.</strong>
        </div>
        }
      </section>

      <div class="movement-footnote">
        @if (cash.totals.movementsCount > cash.recentMovements.length) {
        <p>Mostrando as {{ cash.recentMovements.length }} linhas mais recentes do período. O total completo usado nos cards continua sendo calculado com todos os movimentos importados.</p>
        } @else {
        <p>Estas linhas correspondem a todos os movimentos importados para o período.</p>
        }
      </div>

      @if (cash.recentMovements.length === 0) {
      <div class="empty-state">
        <strong>Nenhum movimento importado para este período.</strong>
        <p>Use "Atualizar fechamento" para buscar os produtos vendidos e solicitar os relatórios financeiros oficiais do Mercado Pago.</p>
      </div>
      }
    </section>
    } @else {
    <section class="card table-wrap">
      <h2>Extrato linha a linha</h2>
      <div class="empty-state">
        <strong>Extrato Mercado Pago ainda não carregado.</strong>
        <p>Quando o extrato estiver salvo, esta aba mostrará cada linha e o pedido correspondente.</p>
      </div>
    </section>
    }
  </section>
  }

  <!-- ═══════════════ VENDAS ═══════════════ -->
  @if (tab() === 'vendas') {
  <section id="panel-vendas" class="tab-panel" role="tabpanel" aria-labelledby="tab-vendas" tabindex="-1">
    @if (periodBusiness(); as biz) {
    <section class="kpi-grid">
      <article class="card kpi"><span>Pedidos no período</span><strong>{{ biz.totals.operationsTotal }}</strong></article>
      <article class="card kpi kpi-tone-net"><span>Pedidos positivos</span><strong>{{ biz.totals.profitableCount }}</strong></article>
      <article class="card kpi kpi-tone-return"><span>Pedidos negativos</span><strong>{{ biz.totals.unprofitableCount }}</strong></article>
      <article class="card kpi"><span>Pedidos cancelados</span><strong>{{ biz.totals.cancelledCount }}</strong></article>
      <article class="card kpi"><span>Pedidos devolvidos</span><strong>{{ biz.totals.returnedCount }}</strong></article>
      <article class="card kpi kpi-tone-received"><span>Pedidos com dinheiro recebido</span><strong>{{ biz.totals.receivedCount }}</strong></article>
    </section>

    <section class="product-card-section">
      <div class="section-head">
        <div><span>Pedidos com entrada</span><h2>Produtos que trouxeram dinheiro</h2></div>
        <span class="marketplace-pill"><span class="ml-mark">ML</span>Mercado Livre</span>
      </div>
      <p class="section-caption">Aqui ficam os pedidos que tiveram entrada de dinheiro. Devoluções e débitos sem produto aparecem nas seções abaixo.</p>
      @if (activeProducts(biz).length > 0) {
      <div class="product-card-grid">
        @for (product of activeProducts(biz); track product.operationKey) {
        <app-product-economics-card [product]="product" [listingTypeLabel]="formatListingType(product.listingTypeId)" />
        }
      </div>
      } @else {
      <div class="empty-state"><strong>Nenhuma venda ativa com receita no período.</strong><p>As operações foram devoluções ou ajustes (veja abaixo).</p></div>
      }
    </section>

    @if (returnedProducts(biz).length > 0) {
    <section class="card table-wrap">
      <h2>Pedidos devolvidos no período ({{ returnedProducts(biz).length }})</h2>
      <p class="section-caption">
        Vendas que foram <strong>estornadas</strong>: o dinheiro voltou para o comprador.
        O impacto negativo inclui o valor estornado + taxas que o Mercado Livre não devolve.
      </p>
      <table>
        <caption class="visually-hidden">Devoluções do período e impacto financeiro</caption>
        <thead><tr><th scope="col">Produto</th><th scope="col">Valor estornado</th><th scope="col">Taxas não devolvidas</th><th scope="col">Impacto no resultado</th></tr></thead>
        <tbody>
          @for (product of returnedProducts(biz); track product.operationKey) {
          <tr>
            <td><strong>{{ product.itemTitle ?? 'Produto sem título' }}</strong><div class="item-sub">{{ product.itemId ?? product.operationKey }}</div></td>
            <td [class.negative-money]="true">-{{ product.returnCostAmount | currency : 'BRL' : 'symbol' : '1.2-2' }}</td>
            <td [class.negative-money]="product.platformFeeAmount > 0">-{{ product.platformFeeAmount | currency : 'BRL' : 'symbol' : '1.2-2' }}</td>
            <td [class.negative-money]="product.netResult < 0"><strong>{{ product.netResult | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong></td>
          </tr>
          }
        </tbody>
      </table>
    </section>
    }

    @if (feeOnlyProducts(biz).length > 0) {
    <section class="card table-wrap">
      <h2>Débitos sem produto vinculado ({{ feeOnlyProducts(biz).length }})</h2>
      <p class="section-caption">Tarifas ou faturas do ML/MP que não correspondem a uma venda específica.</p>
      <table>
        <caption class="visually-hidden">Custos de fatura sem produto vinculado</caption>
        <thead><tr><th scope="col">Lançamento</th><th scope="col">Custo</th></tr></thead>
        <tbody>
          @for (product of feeOnlyProducts(biz); track product.operationKey) {
          <tr>
            <td>{{ product.itemTitle ?? 'Tarifa/fatura ML/MP' }}<div class="item-sub">{{ product.operationKey }}</div></td>
            <td [class.negative-money]="true">{{ product.netResult | currency : 'BRL' : 'symbol' : '1.2-2' }}</td>
          </tr>
          }
        </tbody>
      </table>
    </section>
    }

    <div class="two-col-panels">
      <article class="card table-wrap">
        <h2>Pedidos que mais ajudaram o período</h2>
        <table>
          <caption class="visually-hidden">Pedidos com maior resultado positivo</caption>
          <thead><tr><th scope="col">Produto</th><th scope="col">Recebido</th><th scope="col">Custos</th><th scope="col">Resultado</th><th scope="col">% lucro</th></tr></thead>
          <tbody>
            @for (row of biz.topProfits; track row.operationKey) {
            <tr>
              <td>{{ row.itemTitle ?? '-' }}</td>
              <td>{{ row.received | number : '1.2-2' }}</td>
              <td>{{ row.totalCosts | number : '1.2-2' }}</td>
              <td>{{ row.netResult | number : '1.2-2' }}</td>
              <td>{{ marginPct(row.netResult, row.received) === null ? '-' : (marginPct(row.netResult, row.received) | number : '1.2-2') + '%' }}</td>
            </tr>
            }
          </tbody>
        </table>
      </article>
      <article class="card table-wrap">
        <h2>Pedidos que mais prejudicaram o período</h2>
        <table>
          <caption class="visually-hidden">Pedidos com maior resultado negativo</caption>
          <thead><tr><th scope="col">Produto</th><th scope="col">Recebido</th><th scope="col">Custos</th><th scope="col">Resultado</th><th scope="col">Motivo</th></tr></thead>
          <tbody>
            @for (row of biz.topLosses; track row.operationKey) {
            <tr>
              <td>{{ row.itemTitle ?? '-' }}</td>
              <td>{{ row.received | number : '1.2-2' }}</td>
              <td>{{ row.totalCosts | number : '1.2-2' }}</td>
              <td>{{ row.netResult | number : '1.2-2' }}</td>
              <td>{{ formatReason(row.cancelReason) }}</td>
            </tr>
            }
          </tbody>
        </table>
      </article>
    </div>

    <section class="card table-wrap">
      <h2>Motivos de cancelamento e devolução</h2>
      <table>
        <caption class="visually-hidden">Motivos de cancelamento e devolução do período</caption>
        <thead><tr><th scope="col">Motivo</th><th scope="col">Ocorrências</th><th scope="col">Valor cancelado</th><th scope="col">Valor devolvido</th></tr></thead>
        <tbody>
          @for (row of biz.cancelReasons; track row.reason) {
          <tr><td>{{ formatReason(row.reason) }}</td><td>{{ row.qty }}</td><td>{{ row.cancelledAmount | number : '1.2-2' }}</td><td>{{ row.returnedAmount | number : '1.2-2' }}</td></tr>
          }
        </tbody>
      </table>
    </section>

    <section class="card no-print">
      <p>Use o seletor no topo para trocar o período desta análise.</p>
      @if (message()) { <p>{{ message() }}</p> }
      @if (error()) { <p class="error">{{ error() }}</p> }
    </section>
    }
  </section>
  }

  <!-- ═══════════════ HISTÓRICO ═══════════════ -->
  @if (tab() === 'historico') {
  <section id="panel-historico" class="tab-panel" role="tabpanel" aria-labelledby="tab-historico" tabindex="-1">
    @if (insights(); as overview) {
    <section class="card history-panel">
      <div class="section-title-row">
        <div>
          <span class="eyebrow">Evolução mês a mês</span>
          <h2>Histórico financeiro mensal</h2>
          <p>Janela analisada: {{ formatDate(overview.period.fromDate) }} até {{ formatDate(overview.period.toDate) }}.</p>
        </div>
      </div>

      <div class="history-explainer">
        <article>
          <span>Entenda o histórico</span>
          <strong>Entrada - saída = saldo</strong>
          <p>Barras verdes são o que entrou, vermelhas o que saiu. Se a barra vermelha for maior, o mês fechou negativo.</p>
        </article>
        <article>
          <span>Mês com mais entrada</span>
          <strong>{{ historyBestMonth() }}</strong>
          <p>Mês com maior valor recebido no histórico.</p>
        </article>
        <article [class.warning]="historyNegativeMonths() > 0">
          <span>Meses negativos</span>
          <strong>{{ historyNegativeMonths() }} mês(es) negativo(s)</strong>
          <p>{{ historyAttentionText() }}</p>
        </article>
      </div>

      @if (chartData().length > 0) {
      <div class="chart-container">
        <svg role="img" aria-labelledby="history-chart-title history-chart-desc" [attr.viewBox]="'0 0 ' + (chartData().length * 80 + 60) + ' 230'" class="bar-chart" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">
          <title id="history-chart-title">Evolução mensal de entradas e saídas</title>
          <desc id="history-chart-desc">Gráfico de barras comparando o dinheiro que entrou e saiu em cada mês do histórico. A tabela logo abaixo mostra os mesmos valores em texto.</desc>
          <g transform="translate(40, 20)">
            @for (col of chartData(); track col.label; let i = $index) {
              @let x = i * 80;
              @let barW = 28;
              @let gap = 4;

              <text [attr.x]="x + barW / 2" [attr.y]="205" text-anchor="middle" class="chart-label">{{ col.label }}</text>

              <rect [attr.x]="x" [attr.y]="190 - col.ePct" [attr.width]="barW" [attr.height]="col.ePct" class="chart-bar chart-bar-entry" rx="3" />
              <rect [attr.x]="x + barW + gap" [attr.y]="190 - col.sPct" [attr.width]="barW" [attr.height]="col.sPct" class="chart-bar chart-bar-exit" rx="3" />

              @if (col.net !== 0) {
                @let netY = col.netPositive ? 190 - col.nPct - 8 : 190 + 6;
                <text [attr.x]="x + barW + gap / 2" [attr.y]="netY" text-anchor="middle" class="chart-net-label">{{ col.net | currency : 'BRL' : 'symbol' : '1.0-0' }}</text>
              }
            }
          </g>
        </svg>
        <div class="chart-legend">
          <span class="legend-item"><span class="legend-dot dot-entry"></span> Entrou</span>
          <span class="legend-item"><span class="legend-dot dot-exit"></span> Saiu</span>
        </div>
      </div>
      }

      <div class="kpi-grid">
        <article class="card kpi kpi-tone-received"><span>Entrou no histórico</span><strong>{{ overview.totals.totalReceived | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong></article>
        <article class="card kpi kpi-tone-return"><span>Saiu do histórico</span><strong>{{ overview.totals.totalDebits | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong></article>
        <article class="card kpi kpi-tone-fee"><span>Taxas/fretes encontrados</span><strong>{{ overview.totals.totalCosts | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong></article>
        <article class="card kpi highlight kpi-tone-net"><span>Saldo líquido do histórico</span><strong>{{ overview.totals.totalNetResult | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong></article>
      </div>

      <table>
        <caption class="visually-hidden">Histórico financeiro mensal</caption>
        <thead><tr><th scope="col">Mês</th><th scope="col">Entrou</th><th scope="col">Saiu</th><th scope="col">Taxas/fretes</th><th scope="col">% taxas/entradas</th><th scope="col">Saldo</th><th scope="col">Leitura</th></tr></thead>
        <tbody>
          @for (row of overview.monthly; track row.month) {
          <tr [class.attention-row]="row.netResult < 0 || (row.costPct !== null && row.costPct >= 30)">
            <td>{{ formatMonth(row.month) }}</td>
            <td>{{ row.totalReceived | currency : 'BRL' : 'symbol' : '1.2-2' }}</td>
            <td>{{ row.totalDebits | currency : 'BRL' : 'symbol' : '1.2-2' }}</td>
            <td>{{ row.totalCosts | currency : 'BRL' : 'symbol' : '1.2-2' }}</td>
            <td>{{ row.costPct === null ? '-' : (row.costPct | number : '1.2-2') + '%' }}</td>
            <td [class.negative-money]="row.netResult < 0"><strong>{{ row.netResult | currency : 'BRL' : 'symbol' : '1.2-2' }}</strong></td>
            <td>
              @if (row.netResult < 0) {
                <span class="status-chip danger">Saída maior que entrada</span>
              } @else if (row.costPct !== null && row.costPct >= 30) {
                <span class="status-chip warning">Taxas altas</span>
              } @else {
                <span class="status-chip ok">Dentro do esperado</span>
              }
            </td>
          </tr>
          }
        </tbody>
      </table>
    </section>
    }

    <section class="card table-wrap">
      <h2>Fechamentos já salvos</h2>
      <p class="section-caption">Registros oficiais salvos quando um período foi fechado.</p>
      @if (closures().length > 0) {
      <table>
        <caption class="visually-hidden">Fechamentos de período salvos</caption>
        <thead><tr><th scope="col">Período</th><th scope="col">Fechado por</th><th scope="col">Fechado em</th></tr></thead>
        <tbody>
          @for (closure of closures(); track closure.id) {
          <tr><td>{{ formatDate(closure.periodStart) }} até {{ formatDate(closure.periodEnd) }}</td><td>{{ closure.closedBy }}</td><td>{{ formatDateTime(closure.closedAt) }}</td></tr>
          }
        </tbody>
      </table>
      } @else {
      <div class="empty-state"><strong>Nenhum fechamento salvo ainda.</strong><p>Quando um período estiver conferido, é possível fechá-lo para guardar o snapshot oficial.</p></div>
      }
    </section>

    <section class="card no-print">
      <p>{{ loading() ? 'Carregando...' : '' }}</p>
      @if (message()) { <p>{{ message() }}</p> }
      @if (error()) { <p class="error">{{ error() }}</p> }
    </section>
  </section>
  }

  <!-- ═══════════════ CONFIGURAÇÕES (PAINEL) ═══════════════ -->
  @if (showSettings()) {
  <div class="settings-overlay" (click)="closeSettings()" (keydown)="handleSettingsKeydown($event)">
    <div #settingsPanel class="settings-panel card" role="dialog" aria-modal="true" aria-labelledby="settings-title" aria-describedby="settings-description" tabindex="-1" (click)="$event.stopPropagation()">
      <div class="settings-head">
        <h2 id="settings-title">Comissão dos funcionários</h2>
        <button class="btn btn-secondary" type="button" (click)="closeSettings()">Fechar</button>
      </div>
      <p id="settings-description" class="section-caption">Defina o percentual de cada funcionário. Use valores entre 0 e 100.</p>
      @if (employees().length === 2 && !employees()[0].id && !employees()[1].id) {
      <div class="cash-note">
        <strong>Configuração padrão carregada.</strong>
        Rodrigo e Maria apareceram com os percentuais iniciais para você poder salvar no banco, caso ainda não exista cadastro.
      </div>
      }
      <table>
        <caption class="visually-hidden">Configuração de comissão por funcionário</caption>
        <thead><tr><th scope="col">Funcionário</th><th scope="col">Status</th><th scope="col">% comissão</th><th scope="col">Base usada</th><th scope="col">Salvar</th></tr></thead>
        <tbody>
          @for (employee of employees(); track employee.name) {
          <tr>
            <th scope="row">{{ employee.name }}</th>
            <td>{{ employee.status }}</td>
            <td>
              <label class="visually-hidden" [for]="'commission-rate-' + employee.name">Percentual de comissão de {{ employee.name }}</label>
              <input class="input-inline" [id]="'commission-rate-' + employee.name" [name]="'commissionRate' + employee.name" type="number" min="0" max="100" step="0.01" inputmode="decimal" [value]="employeeRateDrafts()[employee.name]" (input)="setEmployeeRateDraft(employee.name, $any($event.target).value)" />
            </td>
            <td>{{ employee.calculation_base }}</td>
            <td><button class="btn btn-secondary" type="button" (click)="saveEmployeeRate(employee)" [disabled]="savingItem()['emp:' + employee.name]">{{ savingItem()['emp:' + employee.name] ? 'Salvando...' : 'Salvar' }}</button></td>
          </tr>
          }
        </tbody>
      </table>
    </div>
  </div>
  }
</main>
